import { FC } from 'react';
import { OlvLockState } from '../hooks/useOlvLockState';
import {
  OlvLockContainer,
  OlvLockIcon,
  OlvLockMessage,
  OlvLockSubMessage
} from './olvLockOverlayStyles';

interface OlvLockOverlayProps {
  lockState: Exclude<OlvLockState, 'unlocked'>;
}

interface LockMessages {
  ariaLabel: string;
  main: string;
  sub: string;
}

const MESSAGES: Record<Exclude<OlvLockState, 'unlocked'>, LockMessages> = {
  pending_rulings: {
    ariaLabel: 'Waiting for other documents: Complete rulings on all other documents first',
    main: 'Waiting for Other Documents',
    sub: 'Complete rulings on all other documents in this submission before reviewing this offer letter.'
  },
  blocked_edited: {
    ariaLabel: 'Checklist locked: An edited document was found in this submission',
    main: 'Checklist Locked',
    sub: 'An edited document was found in this submission. This offer letter has been automatically ruled as Insufficient.'
  },
  blocked_terminal_ol: {
    // ITL-2769: generic copy — fires for both cascade targets (sibling OL
    // marked terminal via the BE cascade) AND the originating proof (which
    // the analyst just submitted). The localFormData signal was unreliable
    // as a way to distinguish the two; generic copy works correctly for
    // both cases.
    ariaLabel: 'Checklist locked: this offer letter has already been ruled and cannot be edited',
    main: 'Checklist Locked',
    sub: 'This offer letter has already been ruled and cannot be edited.'
  }
};

const OlvLockOverlay: FC<OlvLockOverlayProps> = ({ lockState }) => {
  // ITL-2769: both blocked_edited and blocked_terminal_ol are terminal
  // ("permanent") lock states. They share the alert semantics and the
  // close-icon. pending_rulings is a transient ("waiting") state.
  const isPermanent = lockState === 'blocked_edited' || lockState === 'blocked_terminal_ol';
  const msg = MESSAGES[lockState];

  return (
    <OlvLockContainer
      data-testid="olv-lock-overlay"
      $isPermanent={isPermanent}
      role={isPermanent ? 'alert' : 'status'}
      aria-live={isPermanent ? 'assertive' : 'polite'}
      aria-label={msg.ariaLabel}
    >
      <OlvLockIcon
        src={`/assets/images/${isPermanent ? 'icon-circle-close' : 'hourglass'}.svg`}
        alt={isPermanent ? 'Blocked' : 'Waiting'}
        $isPermanent={isPermanent}
      />
      <OlvLockMessage>{msg.main}</OlvLockMessage>
      <OlvLockSubMessage>{msg.sub}</OlvLockSubMessage>
    </OlvLockContainer>
  );
};

export default OlvLockOverlay;
